import { ref } from 'vue';
import {
  getPredictionById,
  resolvePredictiveMessages,
} from '@/common/bot/flow-frameworks/ChatMessagePredictiveSuggestions.static';
import type { PredictiveSuggestionConfig } from '@/common/api/models.pleased';
import type { BaseMessage, BaseSystemMessage } from '@/library/message/types';
import { toArr } from '@/common/bot/flow-frameworks/ChatFramework.utils';
import { $pr } from '@/pleased-sdk/PleasedReporting';
import { EVENT_TYPES, FEATURES_EVENTS, useEventLogger } from '@/composables/useEventLogger';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

/**
 * Composable for handling predictive suggestions
 * Resolves and provides predictive messages based on user's transaction history
 */
export function usePredictiveSuggestions() {
  const cachedPredictiveOptions = ref<{ messages: BaseMessage[]; matched: any } | null>(null);

  /**
   * Resolves predictive suggestion messages based on configuration
   * Checks user's recent transactions and account status to suggest relevant help topics
   *
   * @param config - Predictive suggestion configuration from brand config
   * @returns Object containing messages array and matched prediction info
   */
  const resolvePredictiveOptions = async (config?: null | PredictiveSuggestionConfig[]) => {
    const logger = useEventLogger();

    // Return cached result if it already exists
    if (cachedPredictiveOptions.value) {
      return cachedPredictiveOptions.value;
    }

    const message = await resolvePredictiveMessages(config).catch((e) => {
      captureEventException(e, ErrorTrackingEvent.ChatbotPredictiveOptions);
      return [];
    });
    const messages = toArr(message || []).flat() as BaseMessage[];

    if (!messages.length) {
      const emptyResult = { messages: [], matched: null };
      cachedPredictiveOptions.value = emptyResult;
      return emptyResult;
    }

    // Find the matched predictive option (there should be only one)
    const matched = messages.find((m) => (m as BaseSystemMessage).config?.matchedPredictiveOption) as
      | BaseSystemMessage
      | undefined;

    if (matched && matched.type) {
      $pr.predictionMatched(getPredictionById(matched?.config?.matchedPredictiveOption!)!.selectionId);
      logger.track({
        type: EVENT_TYPES.FEATURE,
        name: FEATURES_EVENTS.PREDICTIVE_OPTIONS_MATCHED,
        selection: matched.type,
      });
    }

    const result = {
      messages,
      matched: matched?.config?.matchedPredictiveOption
        ? {
            type: matched.config.matchedPredictiveOption,
            message: matched,
          }
        : null,
    };

    cachedPredictiveOptions.value = result;
    return result;
  };

  return {
    resolvePredictiveOptions,
  };
}
